import { useState, useEffect, useCallback } from 'react';
import { useOnlineStatus } from '../../offline/ConnectivityContext';
import { getCachedMeta, setCachedMeta } from '../../offline/localStore';

/* ─────────────────────────────────────────────
   Fetch/cache hook générique pour la collection app_metadata ({_id, type,
   data}) — petits référentiels de config type liste d'options pour un
   <select> (ex. type="building_types", "machine_types"). Retourne
   directement `data`.

   Mis en cache dans IndexedDB (table `meta`, offline/db.js) à chaque fetch
   réussi, et relu depuis ce cache si hors-ligne ou si le fetch échoue —
   sinon un formulaire d'audit ouvert hors-ligne (ex. choix du type de
   machine) se retrouverait avec une liste vide et bloquerait la saisie.
───────────────────────────────────────────── */
export function useAppMetadata(metadataType) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const { isOnline } = useOnlineStatus();
    const cacheKey = `app_metadata:${metadataType}`;

    const reload = useCallback(async () => {
        setLoading(true);
        if (!isOnline) {
            const cached = await getCachedMeta(cacheKey);
            setData(Array.isArray(cached) ? cached : []);
            setLoading(false);
            return;
        }
        try {
            const r = await fetch(`${process.env.REACT_APP_API_URL}/get_app_metadata/${metadataType}`);
            const json = await r.json();
            const list = Array.isArray(json) ? json : [];
            setData(list);
            setCachedMeta(cacheKey, list).catch(() => {});
        } catch {
            const cached = await getCachedMeta(cacheKey);
            setData(Array.isArray(cached) ? cached : []);
        } finally {
            setLoading(false);
        }
    }, [cacheKey, isOnline, metadataType]);

    useEffect(() => { reload(); }, [reload]);

    return { data, loading, reload };
}
